<template lang="pug">
	.ssml-insertion--overdubbing-tool
		Popup(v-if="popupVisible", :popupVisible.sync="popupVisible", :width="720", :height="480", :autosize="true", :title="$tCaption('Overdubbing tool')", @hiding="close", :closeOnOutsideClick="false")
			.ssml-insertion--overdubbing-tool.flex-col.space-large
				.flex-col.space
					.flex-row
						.adt-tab(:class="{ 'active': isSentence }", @click="mode = 'sentence'") {{$tCaption('Generate sentence')}}
						.adt-tab(:class="{ 'active': isContextual }", @click="mode = 'contextual'") {{$tCaption('Contextual overdub')}}
					.font-small.grey(v-if="isSentence") {{$tCaption('For such use-cases where you want to generate new sentences based on the given audio input.')}}
					.font-small.grey(v-if="isContextual") {{$tCaption('For such use-cases where you need to insert or edit a few words within a given audio input.')}}
					.flex-row.space-medium
						.flex-col.flex-1
							.font-big.upper.grey(v-if="isContextual") {{$tCaption('Prefix')}}
							.prefix.flex-row.space(:class="{ 'disabled-controls': prefix.processing }")
								.flex-1.clickable.hoverable.flex-row.align-center.center.adt-button.padded-xlg.record-button.space.nowrap(@click="toggleRecord(prefix)", :class="{ recording: prefix.isRecording }")
									fas(size="lg", :icon="prefix.processing ? 'spinner' : (prefix.isRecording? 'square' : 'fas fa-microphone fa-xs')", :spin=" prefix.processing")
									span {{prefix.processing  ? $tCaption('Processing') : (prefix.text ? $tCaption('Re-take sample') : $tCaption('Record sample'))}}
								.clickable.hoverable.flex-row.align-center.center.adt-button.padded-xlg.record-button.space.nowrap(@click="uploadFile(prefix)", :title="$tCaption('Upload audio')")
									fas(icon="upload")
						.flex-col.flex-1(v-if="isContextual")
							.font-big.upper.grey {{$tCaption('Suffix')}}
							.suffix.flex-row.space(:class="{ 'disabled-controls': suffix.processing }")
								.flex-1.clickable.hoverable.flex-row.align-center.center.adt-button.padded-xlg.record-button.space.nowrap(@click="toggleRecord(suffix)", :class="{ recording: suffix.isRecording }")
									fas(size="lg", :icon="suffix.processing ? 'spinner' : (recorder.isRecording && suffix.isRecording ? 'square' : 'fas fa-microphone fa-xs')", :spin="suffix.processing")
									span {{suffix.processing ? $tCaption('Processing') : (suffix.text ? $tCaption('Re-take sample') : $tCaption('Record sample'))}}
								.clickable.hoverable.flex-row.align-center.center.adt-button.padded-xlg.record-button.space.nowrap(@click="uploadFile(suffix)", :title="$tCaption('Upload audio')")
									fas(icon="upload")
						.flex-col.flex-1(:class="{ 'disabled-controls': !canPlay || isPlaying === 1 || isGenerating }")
							.font-big.upper.grey(v-if="isContextual") {{$tCaption('Preview')}}
							.flex-row.space
								.flex-1.clickable.hoverable.flex-row.align-center.center.adt-button.padded-xlg.record-button.space.nowrap(@click="togglePlay")
									fas(size="lg", :icon="isPlaying === 1 ? 'spinner' : (isPlaying === 2 ? 'square' : 'fas fa-play fa-xs')", :spin="isPlaying === 1")
									span {{isPlaying > 0 ? $tCaption('Stop', '@actions') : $tCaption('Play generated audio')}}
								.clickable.hoverable.flex-row.align-center.center.adt-button.padded-xlg.record-button.space.nowrap(@click="download", :title="$tCaption('Download preview')")
									fas(icon="download")
					.flex-row.space-medium
						.flex-1.flex-col.field(v-if="prefix.blob", :class="{ 'disabled-controls': prefix.processing }")
							.font-mini.upper.grey {{`${isContextual ? $tCaption('Recognized sample text (prefix)') : $tCaption('Recognized sample text')}`}}: 
							DxTextBox(:value.sync="prefix.text")
						.flex-1.flex-col.field(v-if="suffix.blob && isContextual", :class="{ 'disabled-controls': suffix.processing }")
							.font-mini.upper.grey {{`${isContextual ? $tCaption('Recognized sample text (suffix)') : $tCaption('Recognized sample text ')}`}}: 
							DxTextBox(:value.sync="suffix.text")
				.flex-col.space(v-if="prefix.blob || suffix.blob")
					.flex-row.space-medium
						.flex-col.field.flex-1
							.font-mini.upper.grey {{$tCaption('Text to generate')}}:
							DxTextBox(:value.sync="textToGenerate", :placeholder="$tCaption('Write something here..')", valueChangeEvent="input", @value-changed="() => ssmlBase64 = null")
						.flex-col.field
							.font-mini.upper.grey {{$tCaption('Gender')}}:
							DxSelectBox(:data-source="genders", valueExpr="id", displayExpr="text", :value.sync="gender", :width="80")
				Loader(v-if="isGenerating || isSaving", text="", :fullscreen="true")
			template(slot="actions-right")
				DxButton(text="Save", type="default", @click="() => save()", :disabled="!canPlay || isSaving || isGenerating")
				DxButton(text="Cancel", @click="() =>{ stopRecording(true); popupVisible = false; }")
			template(slot="actions-left")
				.flex-row.align-center.space-large
					DxCheckBox(:value.sync="recorder.listeningMic.value", :text="$tCaption('Mic preview')")
					VuMeter(ref="vumeter", orientation="horizontal", :isPlaying="recorder.isRecording || recorder.listeningMic.value", :filters="{ stereoAnalyser: recorder.stereoAnalyser }", :compact="true", size="180px")
		FileUploader(ref="fileUploader", :isUIHidden="true", @upload="onFileUpload", :accept="['.wav', '.mp3', '.ogg', '.riff']")
</template>

<script>
	import Popup from 'app-core/Popup.vue';
	import DxButton from 'devextreme-vue/button';
	import DxCheckBox from 'devextreme-vue/check-box';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxSelectBox from 'devextreme-vue/select-box';
	import storage from 'storage';
	import recorderWorker from 'worker-loader!recorder/recorderWorker.js';
	import Recorder from 'recorder/recorder';
	import audioHelper from 'view/audioTracker/audio-helper';
	import FileUploader from 'app-core/FileUploader.vue';
	import { saveAs } from 'file-saver';
	import Loader from 'app-core/Loader.vue'
	import VuMeter from 'view/audioTracker/AudioTrackerVUMeter.vue';

	export default {
		translationCategory: 'insertion.SSMLOverdubbingTool',
		name: 'ssml-insertion--overdubbing',
		props: ['voice', 'tracker'],
		components: { Popup, DxButton, DxCheckBox, DxTextBox, DxSelectBox, FileUploader, Loader, VuMeter },
		data() {
			return {
				mode: 'sentence',
				popupVisible: false,
				recorder: {
					audioContext: new AudioContext(),
					instance: null,
					stream: null,
					processing: false,
					listeningMic: storage.prop('pronunciation_listeningMic', void 0, false),
					isRecording: false,
					input: null,
					stereoAnalyser: null
				},
				isLoading: 0,
				isPlaying: 0,
				prefix: {
					blob: null,
					base64: null,
					text: null,
					isRecording: false,
					processing: false,
					azureFile: null,
					link: null
				},
				suffix: {
					blob: null,
					base64: null,
					text: null,
					isRecording: false,
					processing: false,
					azureFile: null,
					link: null
				},
				ssmlBase64: null,
				textToGenerate: null,
				gender: 'male',
				genders: [{ id: 'male', text: this.$tCaption('Male') }, { id: 'female', text: this.$tCaption('Female') }],
				isSaving: false,
				activeItem: null,
				isGenerating: false
			}
		},
		computed: {
			canPlay() {
				return !!(this.isContextual ? (this.prefix.blob && this.suffix.blob) : this.prefix.blob) && this.textToGenerate;
			},
			isContextual() {
				return this.mode === 'contextual'
			},
			isSentence() {
				return this.mode === 'sentence';
			},
			tag() {
				const prefixInfo = `prefixAudioContent="${this.$Data.encodeHTML(this.prefix.link)}" prefixText="${this.prefix.text || 'none'}"`;
				const suffixInfo = `suffixAudioContent="${this.$Data.encodeHTML(this.suffix.link)}" suffixText="${this.suffix.text || 'none'}"`;
				return `<mstts:ttsembedding ${prefixInfo} ${this.isContextual ? suffixInfo : ''} format="wav"><lang xml:lang="${this.language}"> ${this.textToGenerate} </lang></mstts:ttsembedding>`;
			},
			ssml() {
				return `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis"  xmlns:mstts="http://www.w3.org/2001/mstts" xml:lang="${this.language}">`
					+ `<voice xml:lang="${this.language}" xml:gender="${this.$Data.capitalize(this.gender || 'male')}" name="en-US-ZeroshotOverDubNeural"> ${this.tag} </voice>`
					+ `</speak>`;
			},
			language() {
				return this.tracker?.language || 'en-US';
			}
		},
		watch: {
			'recorder.listeningMic.value'() {
				this.initRecording(!this.recorder.isRecording);
			},
			prefix: {
				handler() {
					this.ssmlBase64 = null;
				},
				deep: true
			},
			suffix: {
				handler() {
					this.ssmlBase64 = null;
				},
				deep: true
			}
		},
		mounted() {
			if (__DEBUG__)
				window.ssmlOverdubbingTool = this;
		},
		destroyed() {
			this.stopRecording(true);
		},
		methods: {
			async download() {
				if (!this.ssmlBase64) await this.generatePreview();
				if (this.ssmlBase64) {
					saveAs(this.$Data.dataURItoBlob(this.ssmlBase64), `${(this.textToGenerate || '').slice(0, 30)}_preview.wav`);
				}
			},
			async uploadFile(item) {
				this.activeItem = item;
				this.$refs.fileUploader.showPopup();
			},
			async onFileUpload(files) {
				if (__DEBUG__)
					console.debug('OnFileUpload', { files, f: files[0], resampled });
				
				const resampled = await audioHelper.resampleBlob(files[0], 24000, { mono: true });
				await this.setItemBlob(this.activeItem, resampled);
			},
			async generatePreview() {
				this.isGenerating = true;
				try {
					await this.processResults();
					const voice = 'en-US-ZeroshotOverDubNeural';
					const language = this.voice?.locale || 'en-US';
					const r = await this.$Data.postExternalApi('Creative/BuildAudioCustom', {
						Language: language,
						Rate: 1,
						Pitch: 1,
						Voice: voice,
						Ssml: this.ssml,
						ClientProcessId: this.$auth.getGlobalProcessId()
					}).catch(() => {
						this.isPlaying = 0;
					});

					this.ssmlBase64 = r?.AudioData && `data:audio/wav;base64,${r?.AudioData}` || null;
				} finally {
					this.isGenerating = false;
				}
			},
			async togglePlay() {
				if (this.isPlaying) {
					this.stopAudio();
					this.isPlaying = 0;
					return;
				}

				try {
					this.isPlaying = 1;
					this.$player = new Audio();					

					if (!this.ssmlBase64) await this.generatePreview();

					if (!this.ssmlBase64 || !this.isPlaying) {
						this.isPlaying = 0;
						return;
					}

					this.isPlaying = 2;
					this.$player.src = this.ssmlBase64;
					this.$player.play();
					this.$player.addEventListener('ended', () => {
						delete this.$player;
						this.isPlaying = 0;
					}, false);
				} catch {
					this.isPlaying = 0;
				}
			},
			stopAudio() {
				if (this.$player) {
					this.$player.pause();
				}
			},
			close() {
				this.stopRecording(true);
			},
			async processResults() {
				const processItem = async (item) => {
					if (!item.azureFile && item.blob) {
						item.azureFile = await this.$azure.uploadBlobWithInfoResponse(`overdubbing_${this.$Data.uuid()}.wav`, item.blob, 'other');
					}
					if (item.azureFile) {
						item.link = `${item.azureFile?.AzureFileUrl}`;
					}
				};

				await processItem(this.prefix);
				await processItem(this.suffix);
			},
			async save() {
				this.isSaving = true;
				try {
					await this.processResults();
					this.$emit('save', {
						tag: this.tag,
						voiceTag: `<voice xml:lang='en-US' xml:gender='${this.$Data.capitalize(this.gender || 'male')}' name='en-US-ZeroshotOverDubNeural'>${this.tag}</voice>`,
						ssml: this.ssml,
						voice: 'en-US-ZeroshotOverDubNeural',
						locale: 'en-US'
					});
					this.popupVisible = false;
				} finally {
					this.isSaving = false;
				}
			},
			show() {
				if (this.recorder.listeningMic.value) {
					this.initRecording(true);
				}
				this.popupVisible = false;
				this.sourceBase64 = null;
				this.ssmlBase64 = null;
				this.prefix = {
					blob: null,
					base64: null,
					text: null,
					isRecording: false,
					processing: false,
					azureFile: null,
					link: null
				};
				this.suffix = {
					blob: null,
					base64: null,
					text: null,
					isRecording: false,
					processing: false,
					azureFile: null,
					link: null
				};
				setImmediate(() => this.popupVisible = true);
			},
			initRecording(preventRecord) {
				this.stopRecording(true);
				var constraints = { audio: {
					mandatory: {
						googEchoCancellation: false
					},
					optional: []
				}, video: false };
				return new Promise(res => navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
					this.recorder.stream = stream;
					var ctx = this.recorder.audioContext;
					var input = ctx.createMediaStreamSource(stream);

					this.recorder.input = input;

					/* Stereo analyser */
					var stereoAnalyser = new audioHelper.StereoAnalyserNode(ctx);
					stereoAnalyser.fftSize = 512;
					this.recorder.stereoAnalyser = stereoAnalyser;

					if (this.recorder.listeningMic.value) {
						input.connect(stereoAnalyser);
						stereoAnalyser.connect(ctx.destination);
					} else {
						input.connect(stereoAnalyser);
					}

					this.recorder.instance = new Recorder(input, { numChannels: 1, bufferLen: this.$globals.recorderJs.bufferLen }, recorderWorker);
					if (!preventRecord)
						this.recorder.instance.record();

					setImmediate(() => {
						res();
					});
				}).catch((ex) => {
					res();
					throw(ex);
				}));
			},
			async setItemBlob(item, blob) {
				try {
					item.processing = true;
					const voice = this.voice?.id || 'en-US-GuyNeural';
					const language = this.voice?.locale || 'en-US';
					item.azureFile = null;
					item.blob = blob;
					item.base64 = await this.$Data.blob2Base64(blob);
					item.text = (await this.$Data.speechProcessAudio({
						Language: language || 'en-US',
						TranslateTo: null,
						ProfileCode: 'SpeechBatchProcessing',
						AllSteps: true
					}, item.base64))?.FinalText;
				} finally {
					item.processing = false;
				}
			},
			async toggleRecord(item) {
				this.recorder.isRecording = !this.recorder.isRecording;
				item.isRecording = this.recorder.isRecording;
				if (this.recorder.isRecording) {
					this.initRecording();
				} else {
					try {
						this.recorder.processing = true;
						const blob = await this.stopRecording();
						await this.setItemBlob(item, blob);
					} finally {
						this.recorder.processing = false;
						item.processing = false;
					}
				}
			},
			async stopRecording(preventSave) {
				this.stopAudio();
				try {
					if (this.recorder.instance) {
						this.recorder.stream.getAudioTracks()[0].stop();
						this.recorder.instance.stop();
						this.recorder.input.disconnect();
					}

					if (!preventSave) {
						return new Promise((res) => this.recorder.instance.exportWAV((blob) => {
							const resampled = audioHelper.resampleBlob(blob, 24000, { mono: true });
							return res(resampled);
						}));
					}
				} catch(ex) {
					console.error(ex);
				}
			}
		}
	}
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.ssml-insertion--overdubbing-tool {
		.record-button {
			border-radius: 4px;
		}
	}
</style>
